import React, { useCallback, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { ArrowRight, Play, ArrowUpRight, Fan, Gauge, PlaneLanding, Droplets } from 'lucide-react';
import AnimatedCounter from './AnimatedCounter';
import HeroMediaLayer from './HeroMediaLayer';
import { HeroMuteToggle, useIsDesktop, useAutoMuteOnScroll } from './HeroMuteToggle';
import VideoModal from './VideoModal';
import { trackEvent } from '../lib/analytics';

const HERO_POSTER =
  '/images/thumb.jpg';
const HERO_VIMEO_URL = 'https://vimeo.com/1227173730';
const HERO_FULL_VIDEO_URL = 'https://vimeo.com/1184675327';
const HERO_SOUND_CAPTION = 'CFM LEAP fuel-nozzle leak isolated in under 60 minutes';

/**
 * Split-hero home marquee.
 *
 *   Left column  : eyebrow → oversized headline → description → CTAs → KPI row
 *   Right side   : full-bleed CFM LEAP maintenance photo that fades into the
 *                  aviation-blue hero background (no boxed frame)
 *
 * Followed by two flightline-strip rows:
 *   1) "Built for Boeing Procedures / SPL-1473 Approved" summary bar
 *   2) Numbered case-study strip with hover-reveal ROI callouts →
 *      deep-links to the 4 /use-cases/<slug> pages
 *
 * Palette: brighter aviation blue background (slate-900 → blue-900 → slate-800)
 * with a blue/orange accent system — never yellow.  Uses the existing
 * `.cta-group` utility so CTAs stay identical to the rest of the site.
 */
const Hero = ({ onRequestDemo, onRequestQuote, compactTop = false }) => {
  const [muted, setMuted] = useState(true);
  const isDesktop = useIsDesktop();
  const desktopMedia = useRef(null);
  const sectionRef = useRef(null);
  const [videoOpen, setVideoOpen] = useState(false);
  const activeMedia = () => desktopMedia.current;
  const toggleMute = () => {
    const next = !muted;
    setMuted(next);
    const m = activeMedia();
    if (m) m.setMuted(next);
    if (!next) trackEvent('video_unmute', { resource: 'Home hero — CFM LEAP fuel nozzle' });
  };
  const fadeMute = useCallback(() => {
    setMuted(true);
    const m = desktopMedia.current;
    if (m) m.fadeToMute(700);
  }, []);
  useAutoMuteOnScroll(sectionRef, !muted, fadeMute);
  const openFullVideo = () => {
    if (!muted) fadeMute();
    setVideoOpen(true);
    trackEvent('video_watch_full', { resource: 'Home hero — CFM LEAP fuel nozzle' });
  };
  const caseStudies = [
    {
      n: '01',
      icon: Fan,
      label: 'Engine Diagnostics',
      slug: 'engine-diagnostics',
      roi: 'Under 60 min · borescope-and-swap of all 18 nozzles avoided',
    },
    {
      n: '02',
      icon: Gauge,
      label: 'Valve Inspections',
      slug: 'valve-inspections',
      roi: '$6,480 saved · 108% ROI on a single incident',
    },
    {
      n: '03',
      icon: PlaneLanding,
      label: 'Landing Gear',
      slug: 'landing-gear-inspections',
      roi: 'Nitrogen leak located · AOG + specialist dispatch avoided',
    },
    {
      n: '04',
      icon: Droplets,
      label: 'Hydraulic Systems',
      slug: 'hydraulic-systems',
      roi: '$74,400 saved · 1,078% ROI · troubleshoot 96h → 36h',
    },
  ];

  return (
    <>
      {/* -------------------------------- Main hero -------------------------------- */}
      <section
        ref={sectionRef}
        className={`relative isolate overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 pt-[52vh] pb-10 sm:pt-[48vh] sm:pb-14 lg:pb-24 ${compactTop ? 'lg:pt-16' : 'lg:pt-32'}`}
        data-testid="hero-section"
      >
        {/* Photo (with Vimeo video layered on top). Desktop: bleeds off the
            right edge and fades horizontally into the blue background.
            Mobile: fills the top of the hero and fades vertically into the
            copy below (see .hero-media-mask in App.css). */}
        <div
          aria-hidden="true"
          className="hero-media-mask pointer-events-none absolute inset-0 lg:inset-y-0 lg:left-auto lg:right-0 lg:w-[70%] xl:w-[64%]"
        >
          <HeroMediaLayer
            ref={desktopMedia}
            posterImage={HERO_POSTER}
            videoUrl={HERO_VIMEO_URL}
            variant="bleed"
            testid="hero-video"
            objectPosition={isDesktop ? 'right center' : 'center 30%'}
          />
          {/* Gentle blue tint so the footage sits inside the palette */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/30 via-transparent to-slate-900 lg:bg-gradient-to-r lg:from-blue-900/60 lg:via-blue-900/20 lg:to-transparent" />
        </div>

        {/* Mobile mute toggle sits over the media at the top of the hero */}
        <div className="lg:hidden absolute top-24 right-4 z-10">
          <HeroMuteToggle muted={muted} onToggle={toggleMute} caption={HERO_SOUND_CAPTION} testid="hero-unmute-btn-mobile" compact onWatchFull={openFullVideo} />
        </div>

        {/* Grid guide lines */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              'linear-gradient(to right, #7dd3fc 1px, transparent 1px), linear-gradient(to bottom, #7dd3fc 1px, transparent 1px)',
            backgroundSize: '80px 80px',
          }}
        />
        {/* Ambient orange orb for warmth */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-24 right-1/4 w-[420px] h-[420px] rounded-full bg-orange-500/10 blur-3xl"
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center">
            {/* -------------------------------- Left copy ------------------------------- */}
            <div className="lg:col-span-7 xl:col-span-6 animate-fade-in relative">
              {/* Eyebrow */}
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="h-px w-10 bg-blue-400/70" />
                <p className="text-xs sm:text-sm font-semibold tracking-[0.22em] uppercase text-blue-300">
                  Ultrasonic GSTE
                </p>
              </div>

              {/* Headline */}
              <h1 className="text-[2.9rem] sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold text-white leading-[0.98] tracking-tight drop-shadow-[0_2px_20px_rgba(2,6,23,0.6)]">
                Find the Fault.
                <br />
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-300 via-white to-orange-300">
                  Clear the Aircraft.
                </span>
              </h1>

              {/* Description */}
              <p className="mt-4 sm:mt-5 max-w-xl text-[15px] sm:text-lg text-blue-100/90 leading-relaxed drop-shadow-[0_1px_10px_rgba(2,6,23,0.5)]">
                Ultrasonic fault isolation for aviation maintenance. Find leaks, electrical
                arcing, and mechanical faults faster, safely, and without disassembly.
              </p>

              {/* CTAs */}
              <div className="hero-ctas mt-6 sm:mt-7 max-w-md">
                <Button
                  onClick={onRequestDemo}
                  size="lg"
                  className="h-11 sm:h-12 bg-blue-600 hover:bg-blue-700 text-white text-sm sm:text-[15px] font-semibold shadow-2xl shadow-blue-600/30 hover:shadow-blue-600/50 transition-all duration-300 group"
                  data-testid="hero-request-demo"
                >
                  Request Demo
                  <Play
                    className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200"
                    aria-hidden="true"
                  />
                </Button>
                <Button
                  onClick={onRequestQuote}
                  size="lg"
                  className="h-11 sm:h-12 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white text-sm sm:text-[15px] font-semibold shadow-2xl shadow-orange-500/30 hover:shadow-orange-500/50 transition-all duration-300 group"
                  data-testid="hero-request-quote"
                >
                  Request Quote
                  <ArrowRight
                    className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform duration-200"
                    aria-hidden="true"
                  />
                </Button>
              </div>

              {/* Mobile-only KPI row */}
              <div className="mt-7 grid grid-cols-4 gap-x-3 gap-y-6 text-slate-300 lg:hidden">
                {[
                  { id: 'users', end: 85, suffix: '+', duration: 2000, label: 'Aviation Users Worldwide' },
                  { id: 'applications', end: 100, suffix: '+', duration: 2200, label: 'Aviation Mx Applications' },
                  { id: 'manhours', end: 85000, suffix: '+', duration: 2500, label: 'Man-Hours Saved' },
                  { id: 'roi', start: 250, end: 1, suffix: 'st', duration: 2500, label: 'ROI on First Use', accent: true },
                ].map((k, i) => (
                  <div
                    key={k.id}
                    className={`text-left ${i > 0 ? 'border-l border-white/15 pl-3 sm:pl-5' : ''}`}
                    data-testid={`hero-kpi-${k.id}`}
                  >
                    <AnimatedCounter
                      start={k.start}
                      end={k.end}
                      suffix={k.suffix}
                      duration={k.duration}
                      className={k.accent ? 'text-xl sm:text-3xl font-extrabold text-orange-400' : 'text-lg sm:text-2xl font-bold text-white'}
                    />
                    <div className="mt-1 text-[10px] sm:text-xs text-blue-100/70 leading-snug">{k.label}</div>
                  </div>
                ))}
              </div>

              {/* Aircraft-systems tiles with hover-reveal ROI → /use-cases/<slug> */}
              <div className="mt-8 lg:mt-10 grid grid-cols-2 lg:grid-cols-4 gap-3 lg:w-[720px] xl:w-[760px] lg:max-w-none" data-testid="hero-systems-grid">
                {caseStudies.map((cs) => {
                  const Icon = cs.icon;
                  return (
                    <Link
                      key={cs.slug}
                      to={`/use-cases/${cs.slug}`}
                      className="group relative overflow-hidden rounded-xl border border-white/10 bg-white/[0.06] backdrop-blur-sm px-4 py-4 min-h-[112px] lg:min-h-[124px] flex flex-col justify-between transition-colors duration-200 hover:border-blue-400/60 hover:bg-white/10 focus-visible:border-blue-400/60 outline-none"
                      data-testid={`hero-system-${cs.slug}`}
                    >
                      <span className="flex items-center justify-between">
                        <Icon className="w-6 h-6 text-orange-400" aria-hidden="true" />
                        <ArrowUpRight className="w-4 h-4 text-white/30 transition-all group-hover:text-blue-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" aria-hidden="true" />
                      </span>
                      <span className="mt-3 block text-sm font-semibold tracking-wide uppercase text-white/90 leading-tight group-hover:text-blue-200 transition-colors">
                        {cs.label}
                      </span>
                      <span
                        className="pointer-events-none absolute inset-0 flex items-center px-4 py-3 bg-slate-900 translate-y-4 opacity-0 transition-all duration-300 ease-out group-hover:translate-y-0 group-hover:opacity-100 group-focus-visible:translate-y-0 group-focus-visible:opacity-100"
                        data-testid={`hero-system-roi-${cs.slug}`}
                      >
                        <span className="block text-xs font-semibold text-white leading-snug">
                          <span className="text-orange-400">ROI ·</span> {cs.roi}
                          <span className="mt-1.5 flex items-center gap-1 text-[11px] text-blue-300">Read the case study <ArrowUpRight className="w-3 h-3" aria-hidden="true" /></span>
                        </span>
                      </span>
                    </Link>
                  );
                })}
              </div>
            </div>

            {/* -------------------------------- Right side ------------------------------- */}
            {/* Right column intentionally empty on lg+ — the photo lives in the
                absolutely-positioned layer above so it can bleed off-canvas
                and fade into the blue background.  We still emit a linked
                caption anchored inside the visible photo area. */}
            <div className="hidden lg:block lg:col-span-5 xl:col-span-6 relative min-h-[520px] pointer-events-none">
              <Link
                to="/use-cases/engine-diagnostics"
                className="group pointer-events-auto absolute right-2 bottom-2 max-w-xs rounded-lg bg-slate-900/55 backdrop-blur-md border border-white/10 hover:border-blue-300/60 hover:bg-slate-900/70 transition-all px-4 py-3"
                data-testid="hero-photo-case-study-link"
              >
                <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-blue-200/90">
                  Case Study
                </p>
                <p className="mt-0.5 text-white text-sm font-semibold leading-snug">
                  Boeing 737 MAX &middot; CFM LEAP Engine
                </p>
                <span className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-blue-300 group-hover:text-white">
                  Read the case study
                  <ArrowUpRight
                    className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    aria-hidden="true"
                  />
                </span>
              </Link>
              <div className="absolute right-2 top-2 pointer-events-auto">
                <HeroMuteToggle muted={muted} onToggle={toggleMute} caption={HERO_SOUND_CAPTION} testid="hero-unmute-btn" onWatchFull={openFullVideo} />
              </div>
            </div>

          </div>
        </div>
      </section>

      <VideoModal
        open={videoOpen}
        onOpenChange={setVideoOpen}
        videoUrl={HERO_FULL_VIDEO_URL}
        title="CFM LEAP Fuel Nozzle Inspection — Boeing 737 MAX"
      />

      {/* --------------------- Boeing procedures strip --------------------- */}
      <section
        className="relative bg-[#0a1628] border-y border-white/10"
        data-testid="hero-boeing-strip"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
            <div className="md:col-span-5">
              <p className="text-base sm:text-lg font-bold tracking-tight text-white leading-snug">
                Built for Boeing Procedures.
                <br className="hidden sm:block" />
                <span className="text-blue-300"> Trusted on the Flightline.</span>
              </p>
            </div>
            <div className="md:col-span-5">
              <p className="text-sm sm:text-base text-blue-100/70 leading-relaxed">
                The CTRL UL101 (P/N B00033) is referenced for ultrasonic maintenance
                applications on Boeing commercial jet aircraft.
              </p>
            </div>
            <div className="md:col-span-2 flex md:justify-end">
              <div className="inline-flex flex-col items-start md:items-end">
                <span className="text-xs font-bold tracking-[0.2em] uppercase text-orange-300">
                  SPL-1473
                </span>
                <span className="text-xs font-semibold tracking-[0.2em] uppercase text-blue-100/60">
                  Approved
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

    </>
  );
};

export default Hero;
